import { useMemo, useState, useCallback } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import HelpInput from "../../../../Inputs/HelpInput/HelpInput.tsx";
import {
  CentrifugalForce,
  getForceUnits,
  getMassUnits,
  ValueOrNull,
} from "../../../../../utils/helpers/tolerance/GeneralHelper.js";
import {
  useMeasureInUnits,
  useUnits,
} from "../../../../../hooks/useMeasureInUnits.jsx";
import { toFixed } from "../../../../../utils/helpers/NumberHelper.js";
import { HelpOutput } from "../../../../Inputs/HelpDisplay/HelpOutput.tsx";
import { useTranslation } from "react-i18next";
import UnitsRadioGroup from "../../../../Inputs/HelpDisplay/UnitsRadioGroup.jsx";
import ResultBox from "../../../../Styled/Box/ResultBox.jsx";

const CentrifugalForceCalculation = ({ radialSpeed }) => {
  const { t } = useTranslation();

  const unbalanceMassUnits = useMemo(() => getMassUnits(t).slice(0, 3), [t]);
  const forceUnits = useMemo(() => getForceUnits(t), [t]);

  const [radius, setRadius] = useState(null);
  const [massInKg, mass, setMass, unitsOfMass, massUnit, handleMassUnitChange] =
    useMeasureInUnits(null, unbalanceMassUnits[1]);

  const {
    unitLabel: forceUnitLabel,
    unit: forceUnit,
    handleUnitChange: handleForceUnitChange,
  } = useUnits(null, forceUnits[1]);

  const centrifugalForce = useMemo(
    () =>
      toFixed(
        CentrifugalForce(massInKg, radialSpeed, radius * 10e-4 /** convert mm to m */) * forceUnit.value
      ),
    [massInKg, radius, radialSpeed, forceUnit.value]
  );

  const handleRadiusChange = useCallback((e) => {
    const value = +e.target.value;
    if (value >= 0) {
      setRadius(value);
    } else {
      setRadius(0);
    }
  }, []);

  
  const handleMassChange = useCallback((e) => {
    const value = +e.target.value;
    if (value >= 0) {
      setMass(value);
    } else {
      setMass(0);
    } 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Accordion sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h5">
          {t("shared.disbalance.components.centrifugalForceCalculation.title")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography sx={{ my: 2 }} variant="h6">
          {t("shared.disbalance.components.centrifugalForceCalculation.components.partRadius.title")}
        </Typography>
        <HelpInput
          label={<div>R, {t("units.length.mm")}</div>}
          variant="outlined"
          fullWidth
          value={radius || ""}
          type="number"
          onChange={handleRadiusChange}
          sx={{ flexBasis: "100%", my: 2 }}
          helpButtonText={t(
            "shared.disbalance.components.centrifugalForceCalculation.components.partRadius.help"
          )}
        />
         <Typography variant="h6" sx={{ my: 2 }}>
          {t("shared.disbalance.components.centrifugalForceCalculation.components.unbalancedMass.title")}
        </Typography>
        <UnitsRadioGroup
          value={massUnit}
          options={unbalanceMassUnits}
          onChange={handleMassUnitChange}
        />  
        <Typography sx={{ my: 2 }} variant="h6">
          {t("shared.disbalance.components.centrifugalForceCalculation.components.unbalancedMass.title")}
        </Typography>
        <HelpInput
          label={<div>m, {unitsOfMass}</div>}
          variant="outlined"
          fullWidth
          value={mass || ""}
          type="number"
          onChange={handleMassChange}
          sx={{ flexBasis: "100%", my: 2 }}
          slotProps={{
            input: {
              endAdornment: mass ? <Typography ml={2}>{massUnit.label}</Typography> : null,
            },
          }}
          helpButtonText={t(
            "shared.disbalance.components.centrifugalForceCalculation.components.unbalancedMass.help"
          )}
        />
        <Typography variant="h6" sx={{ my: 2 }}>
          {t("units.force.unitSelection")}
        </Typography>
        <UnitsRadioGroup
          value={forceUnit}
          options={forceUnits}
          onChange={handleForceUnitChange}
        />  
        <Typography variant="h6" sx={{ my: 3 }}>
          {t("shared.disbalance.result")}
        </Typography>
        <ResultBox>
          <Typography sx={{ my: 2 }} variant="h6">
            {t("shared.disbalance.components.centrifugalForceCalculation.components.centrifugalForce.title")}
          </Typography>
          <HelpOutput
            label={<div>F, {forceUnitLabel}</div>}
            showEndAdornmentOnChildrenNull
            endAdornment={forceUnitLabel}
            helpButtonText={t(
              "shared.disbalance.components.centrifugalForceCalculation.components.centrifugalForce.help"
            )}
          >
            {ValueOrNull(centrifugalForce)}
          </HelpOutput>
        </ResultBox>
      </AccordionDetails>
    </Accordion>
  );
};

export default CentrifugalForceCalculation;
